/**
 * Map filename to Monaco/syntax-highlighting language identifier.
 */
const LANG_MAP = {
  js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
  py: 'python', java: 'java', c: 'c', cpp: 'cpp', h: 'c', cs: 'csharp',
  html: 'html', css: 'css', json: 'json', xml: 'xml',
  md: 'markdown', sh: 'bash', bash: 'bash', zsh: 'shell',
  sql: 'sql', yaml: 'yaml', yml: 'yaml',
  rb: 'ruby', go: 'go', rs: 'rust', php: 'php',
  txt: 'plaintext', log: 'plaintext', cfg: 'ini', ini: 'ini',
  dockerfile: 'dockerfile', makefile: 'makefile',
};

const SPECIAL_FILENAMES = {
  dockerfile: 'dockerfile',
  makefile: 'makefile',
  '.gitignore': 'plaintext',
  '.env': 'plaintext',
};

export function getLanguage(filename) {
  const lowerName = filename.toLowerCase();
  if (SPECIAL_FILENAMES[lowerName]) return SPECIAL_FILENAMES[lowerName];
  const ext = lowerName.split('.').pop();
  return LANG_MAP[ext] || 'plaintext';
}

/**
 * Build a hierarchical tree from a flat array of file objects.
 *
 * Each file must have a `path` property (e.g. "src/Main.java").
 * Returns an object where:
 *   - file nodes: { ...file, _isFile: true }
 *   - folder nodes: { _children: { ... } }
 */
export function buildFileTree(files) {
  const tree = {};
  for (const file of files) {
    const parts = file.path.split(/[/\\]/);
    let current = tree;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        // Don't overwrite an existing folder node with a file node
        if (!current[part] || !current[part]._children) {
          current[part] = { ...file, _isFile: true };
        }
      } else {
        if (!current[part] || !current[part]._children) {
          current[part] = { _children: {} };
        }
        current = current[part]._children;
      }
    }
  }
  return tree;
}

/**
 * The starter-code upload puts each Java file in the folder its package names (backend
 * utils/starterCodeLayout.js) and returns the moves; this is how staff are told.
 * Returns null when nothing moved.
 */
export function javaFileMovesNotice(moves) {
  if (!moves?.length) return null;
  const headline = moves.length === 1
    ? 'A Java file was moved to the folder its package line names, as Java requires.'
    : `${moves.length} Java files were moved to the folders their package lines name, as Java requires.`;
  return {
    headline: `${headline} Without that, students get no Run or Debug link and no errors across files.`,
    moves: moves.map(({ from, to }) => `${from} → ${to}`),
    note: 'Workspaces already created keep their files.',
  };
}

/**
 * One finding of the starter-code link check (backend utils/starterCodeReferences.js) in
 * plain words: what links where, and what to do about it.
 */
export function missingReferenceText({ file, line, reference, tag, kind, suggestion }) {
  const where = line ? `${file} (line ${line})` : file;
  const verb = tag === 'a' || tag === 'area' ? 'links to' : 'loads';
  // Only capital letters differ, or a backslash in CSS: the suggestion says it all.
  if (kind === 'case' || kind === 'backslash') return `${where} ${verb} ${reference}. ${suggestion}`;
  if (kind === 'local') {
    return `${where} ${verb} ${reference}, a file on the computer the page was written on, which students cannot open. `
      + (suggestion || 'Add the file to the starter code, and link to it there.');
  }
  return `${where} ${verb} ${reference}, which is not in the starter code. ${suggestion || 'Add it, or change the link.'}`;
}

/**
 * The starter code's pages link to files it does not have (the upload response and the
 * starter listing carry `missingReferences` and `missingReferencesTotal`); this is how staff
 * are told. At most `limit` lines, then how many more. Returns null when every link resolves.
 */
export function missingReferencesNotice(missing, total, limit = 10) {
  if (!missing?.length) return null;
  const count = Math.max(total || 0, missing.length);
  return {
    headline: count === 1
      ? 'A link in the starter code does not match its files.'
      : `${count} links in the starter code do not match its files.`,
    lines: missing.slice(0, limit).map(missingReferenceText),
    more: count - Math.min(missing.length, limit),
    note: 'Workspaces already created keep their files.',
  };
}
